import React, { useState } from "react";
import { Eye, EyeOff, ArrowRight, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";

const SignupPage = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+965"); // editable, defaults to Kuwait
  const [phoneLocal, setPhoneLocal] = useState(""); // local digits only
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Format local number with a space every 4 digits (cap at 12 to support longer formats e.g. India 10 digits)
  const formatLocal = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 12);
    if (digits.length <= 4) return digits;
    if (digits.length <= 8) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  };

  const handlePhoneChange = (e) => {
    setPhoneLocal(formatLocal(e.target.value));
  };

  // Sanitize country code as user types: enforce leading +, digits only, 1-4 digits
  const handleCountryCodeChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    setCountryCode(digits ? `+${digits}` : "+");
  };

  const handleCountryCodeBlur = () => {
    // If user emptied it, restore default
    if (countryCode === "+" || countryCode === "") setCountryCode("+965");
  };

  // If user pastes a full international number anywhere, parse it intelligently
  const handlePhonePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text") || "";
    if (!text) return;
    // Detect a leading + or 00 country code
    const trimmed = text.trim();
    let digits = trimmed.replace(/\D/g, "");
    let detectedCode = null;
    if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
      const stripped = trimmed.startsWith("00") ? digits.replace(/^00/, "") : digits;
      // Try common code lengths (1, 2, 3, 4) — pick the longest that leaves at least 6 digits
      for (const len of [4, 3, 2, 1]) {
        if (stripped.length - len >= 6) {
          detectedCode = stripped.slice(0, len);
          digits = stripped.slice(len);
          break;
        }
      }
    } else if (digits.startsWith("965") && digits.length >= 11) {
      detectedCode = "965";
      digits = digits.slice(3);
    }
    e.preventDefault();
    if (detectedCode) setCountryCode(`+${detectedCode}`);
    setPhoneLocal(formatLocal(digits));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    const ccDigits = countryCode.replace(/\D/g, "");
    if (ccDigits.length < 1 || ccDigits.length > 4) {
      setError("Please enter a valid country code (1-4 digits)");
      return;
    }
    const digits = phoneLocal.replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 12) {
      setError("Please enter a valid phone number");
      return;
    }
    const fullPhone = `+${ccDigits} ${formatLocal(digits)}`.trim();
    setLoading(true);
    try {
      await signup(name.trim(), email, fullPhone, password);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="signup-page">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/login-bg.jpg')" }} />
      <div className="absolute inset-0 backdrop-blur-sm bg-black/75" />
      <div className="absolute top-0 left-0 right-0 h-px gold-gradient" />
      <div className="absolute bottom-0 left-0 right-0 h-px gold-gradient" />
      <div className="absolute top-8 left-8 w-12 h-12 border-t border-l border-gold/30" />
      <div className="absolute top-8 right-8 w-12 h-12 border-t border-r border-gold/30" />
      <div className="absolute bottom-8 left-8 w-12 h-12 border-b border-l border-gold/30" />
      <div className="absolute bottom-8 right-8 w-12 h-12 border-b border-r border-gold/30" />

      <Link to="/" className="absolute top-6 left-6 nav-link text-[0.65rem] flex items-center gap-2 z-10">
        <span className="text-gold/60">←</span>
        <span>Back to Home</span>
      </Link>

      <div className="relative z-10 w-full max-w-md px-6 animate-fade-in-up">
        <div className="glass-dark rounded-sm p-10 md:p-12">
          <div className="text-center mb-10">
            <img
              src="/images/mrva-logo-white.png"
              alt="MRVA Productions"
              className="h-20 md:h-24 w-auto object-contain mx-auto mb-4"
            />
            <div className="font-heading text-2xl font-bold tracking-[0.2em] text-white uppercase mb-1">Create Account</div>
            <div className="font-body text-gold text-[0.6rem] tracking-[0.4em] uppercase">Join The Roster</div>
            <div className="gold-divider mt-6" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="input-gold w-full px-4 py-3.5 rounded-sm text-sm font-body"
                required
                minLength={2}
                data-testid="signup-name"
              />
            </div>

            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-gold w-full px-4 py-3.5 rounded-sm text-sm font-body"
                required
                data-testid="signup-email"
              />
            </div>

            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">
                Phone No.
                <span className="text-white/25 normal-case tracking-normal text-[0.6rem] ml-2">
                  · country code editable
                </span>
              </label>
              <div className="input-gold flex items-stretch rounded-sm overflow-hidden focus-within:border-gold/60">
                <input
                  type="text"
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  onBlur={handleCountryCodeBlur}
                  className="w-[5.25rem] px-3 py-3.5 text-sm font-body font-semibold text-gold tracking-wide bg-gold/10 border-r border-gold/20 outline-none focus:bg-gold/15 transition-colors"
                  inputMode="numeric"
                  maxLength={5}
                  aria-label="Country code"
                  data-testid="signup-phone-prefix"
                />
                <input
                  type="tel"
                  value={phoneLocal}
                  onChange={handlePhoneChange}
                  onPaste={handlePhonePaste}
                  placeholder="1234 5678"
                  className="flex-1 bg-transparent px-4 py-3.5 text-sm font-body text-white placeholder-white/30 outline-none border-0"
                  required
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={14}
                  data-testid="signup-phone"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="input-gold w-full px-4 py-3.5 pr-12 rounded-sm text-sm font-body"
                  required
                  minLength={6}
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-gold transition-colors"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3" data-testid="signup-error">
                <p className="font-body text-red-400 text-xs leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full py-4 text-xs rounded-sm flex items-center justify-center gap-3 mt-2"
              data-testid="signup-submit"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  <span>Creating account...</span>
                </div>
              ) : (
                <>
                  <span>Create Account</span>
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="font-body text-white/40 text-xs tracking-wider hover:text-gold transition-colors">
              Already have an account? <span className="text-gold">Sign in</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
